import React from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Button } from '~common/components/button'
import { LoadingIndicator } from '~common/components/loading'
import { useCacheValue } from '~/cache-context'
import type { PersistedImportState } from '~/features/email-importer/services/types'
import { useAppContext, type AppContextType } from '~/app-context'
import Modal from '~common/components/modal'
import { SimpleProgressBar } from '~common/components/progress'
import { formatISODate } from '~common/utils/date-transformation/date-transformations'
import { INVOICE_ESTIMATION_DIVIDER } from './constants'
import { Icon } from '~common/components/icon'
import {
    type ImportProgressBarLogicDependencies,
    ImportProgressBarLogic,
} from './logic'
import { useLogic } from '~common/ui-logic/hooks/logic'

export interface ImportProgressBarProps
    extends ImportProgressBarLogicDependencies {}

export function ImportProgressBar(props: ImportProgressBarProps) {
    const { t } = useTranslation('import-progress-bar')
    const appContext = useAppContext()
    const { logic, state } = useLogic(ImportProgressBarLogic, {
        ...props,
        ...appContext,
    })
    const settings = useCacheValue('settings')
    const fullState = useCacheValue('importProgress')
    if (!fullState) {
        return null
    }
    if (
        fullState.status.import === 'idle' &&
        fullState.status.scan === 'idle' &&
        !fullState.status.paused
    ) {
        return null
    }

    const dateRange = fullState.config.dateRange
    const fromYear = dateRange.fromYear
    const fromMonth = dateRange.fromMonth
    const fromDay = dateRange.fromDay
    const toYear = dateRange.toYear
    const toMonth = dateRange.toMonth
    const toDay = dateRange.toDay
    const fromDate = new Date(Date.UTC(fromYear, fromMonth - 1, fromDay))
    const fromDateObj = fromDate.toISOString()
    const toDate = new Date(Date.UTC(toYear, toMonth - 1, toDay)) // last day of toMonth
    const toDateObj = toDate.toISOString()
    const totalMs = toDate.getTime() - fromDate.getTime()
    const lastProcessedTimestamp = fullState.lastMessageTimestamp

    const isPaused = fullState.status.paused
    const scanStatus = fullState.status.scan
    const importStatus = fullState.status.import
    const isProcessing = scanStatus === 'running' || importStatus === 'running'
    const isError = scanStatus === 'error' || importStatus === 'error'
    const isComplete =
        scanStatus === 'completed' && importStatus === 'completed'

    const lastProcessedObj = lastProcessedTimestamp
        ? new Date(lastProcessedTimestamp)
        : null

    let newCounter = fullState.actualInvoicesFoundThisRun
    let progressPercentage = 0

    const scannedMs = lastProcessedObj
        ? toDate.getTime() - lastProcessedObj.getTime()
        : 0
    let scanPercentage =
        totalMs > 0 ? Math.min(50, Math.round((scannedMs / totalMs) * 50)) : 0

    if (scanStatus === 'completed') {
        scanPercentage = 50
    }

    const totalCandidates = fullState.invoiceCandidatesSeenThisRun || 0
    const processed = fullState.invoiceCandidatesProcessedThisRun || 0
    const deepPercentage =
        scanStatus === 'completed' && totalCandidates > 0
            ? Math.min(50, Math.round((processed / totalCandidates) * 50))
            : 0
    progressPercentage = scanPercentage + deepPercentage

    let statusLabel: string | undefined = undefined
    if (isProcessing) {
        statusLabel = t('status.scanningInbox')
    } else if (isPaused) {
        statusLabel = t('status.scanPaused')
    } else if (isError) {
        statusLabel = t('status.scanError')
    } else if (isComplete) {
        statusLabel = t('status.scanComplete')
    }

    const handleCancel = () => {
        logic.hideConfirmReset()
    }

    const handleResume = async () => {
        await logic.resume()
    }

    return (
        <Container>
            <ProgressBarContainer>
                <TopSection>
                    <LeftSection>
                        <SectionBlock>
                            <SectionLabel>{t('labels.status')}</SectionLabel>
                            <SectionValue>
                                {isProcessing && (
                                    <LoadingIndicator size="small" />
                                )}
                                {statusLabel}
                            </SectionValue>
                        </SectionBlock>
                        <SectionBlock>
                            <SectionLabel>{t('labels.new')}</SectionLabel>
                            <SectionValue>{newCounter}</SectionValue>
                        </SectionBlock>
                        <SectionBlock>
                            <SectionLabel>{t('labels.scanFrom')}</SectionLabel>
                            <SectionValue>
                                {settings?.dateFormat &&
                                    formatISODate(
                                        fromDateObj,
                                        settings?.dateFormat,
                                    )}
                            </SectionValue>
                        </SectionBlock>
                        <SectionBlock>
                            <SectionLabel>{t('labels.scanTo')}</SectionLabel>
                            <SectionValue>
                                {settings?.dateFormat &&
                                    formatISODate(
                                        toDateObj,
                                        settings?.dateFormat,
                                    )}
                            </SectionValue>
                        </SectionBlock>
                    </LeftSection>
                    <RightSection>
                        {(isProcessing || isPaused) && (
                            <Button
                                onClick={logic.showConfirmReset}
                                label={t('buttons.stopScan')}
                                variant="secondary"
                                size="large"
                            />
                        )}
                        {isComplete ? (
                            <Button
                                onClick={logic.handleResetConfirm}
                                label={t('buttons.close')}
                                variant="secondary"
                                size="large"
                            />
                        ) : (
                            <Button
                                onClick={
                                    isPaused || isError
                                        ? handleResume
                                        : logic.pause
                                }
                                label={
                                    isPaused || isError
                                        ? t('buttons.resumeScan')
                                        : t('buttons.pauseScan')
                                }
                                variant="secondary"
                                size="large"
                            />
                        )}
                    </RightSection>
                </TopSection>
                {/* <ProgressRow>
                    <SimpleProgressBar
                        type="square"
                        value={progressPercentage}
                    />
                </ProgressRow> */}
            </ProgressBarContainer>
            <Modal
                isOpen={state.showConfirmReset}
                onClose={handleCancel}
                size="medium"
                closeOnOverlayClick={true}
                closeOnEscape={true}
                showCloseButton={true}
                backdropBlur={true}
                position="center"
            >
                <ModalContent>
                    <ModalTitle>{t('modal.confirmReset')}</ModalTitle>
                    <ModalDescription>
                        {t('modal.resetDescription')}
                    </ModalDescription>
                    <ModalButtonGroup>
                        <Button
                            onClick={handleCancel}
                            label={t('buttons.cancel')}
                            variant="secondary"
                            size="large"
                        />
                        <Button
                            onClick={logic.handleResetConfirm}
                            label={t('buttons.reset')}
                            variant="primary"
                            size="large"
                        />
                    </ModalButtonGroup>
                </ModalContent>
            </Modal>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    width: 100%;
    position: relative;
`
const ProgressBarContainer = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: space-between;
    background-color: ${({ theme }) => theme.colors.background.bg2};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
    gap: ${({ theme }) => theme.spacing[0]};
`

const LeftSection = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[10]};
    flex: 1;
`

const RightSection = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[4]};
    justify-content: flex-end;
`

const FilterButtons = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[2]};
    align-items: center;
`

const ProgressStats = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[10]};
    align-items: center;
`

const ProgressRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    width: 100%;
    position: relative;
`

const DateLabel = styled.span<{ position?: 'left' | 'right' }>`
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.greyScale[100]};
    min-width: 80px;

    ${({ position }) =>
        position === 'left' &&
        css`
            text-align: left;
        `}
    ${({ position }) =>
        position === 'right' &&
        css`
            text-align: right;
        `}
`

const InvoiceLabel = styled.span<{ position?: 'left' | 'right' }>`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.secondary};
    min-width: 80px;
`

const SectionBlock = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[1]};
    flex-direction: column;
`

const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.greyScale[100]};
`

const SectionValue = styled.div`
    display: flex;
    align-items: center;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: nowrap;
    gap: ${({ theme }) => theme.spacing[2]};
`

const TopSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[6]}`};
    box-sizing: border-box;
`

const ModalContent = styled.div`
    box-sizing: border-box;
    padding: ${({ theme }) => theme.spacing[8]};
    text-align: center;
`

const ModalTitle = styled.h2`
    box-sizing: border-box;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const ModalDescription = styled.p`
    box-sizing: border-box;
    margin-bottom: ${({ theme }) => theme.spacing[8]};
    line-height: 1.5;
`

const ModalButtonGroup = styled.div`
    box-sizing: border-box;
    display: flex;
    gap: ${({ theme }) => theme.spacing[4]};
    justify-content: center;
`
